/**
 * GST Registration Section — Phase A (Foundation)
 *
 * Mounts inside the admin CompanyDetailPanel. Purely additive; the legacy
 * GSTFilingTab remains untouched.
 *
 * Design ref: /app/memory/GST_MODULE_CORE_DESIGN.md
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { getApiError } from '../../../utils/errorHelper';
import { gstStatusMeta, effectiveFilingStatus } from '../../../lib/gstStatusCatalog';
import {
  Plus, RefreshCw, Loader2, X, Save, ChevronRight, FileText,
  Receipt, IndianRupee, ShieldCheck, CheckCircle2, Archive,
  ClipboardList, Building2, Link2, Copy, CheckCircle, Upload, ExternalLink, Send, Download,
} from 'lucide-react';

const INR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(Number(n || 0));

const STATUS_CLS = {
  not_started: 'bg-slate-700 text-slate-300',
  documents_awaited: 'bg-amber-500/20 text-amber-300',
  under_preparation: 'bg-blue-500/20 text-blue-300',
  summary_prepared: 'bg-indigo-500/20 text-indigo-300',
  sent_for_client_approval: 'bg-purple-500/20 text-purple-300',
  changes_requested: 'bg-amber-500/20 text-amber-300',
  client_approved: 'bg-emerald-500/20 text-emerald-300',
  ready_for_filing: 'bg-emerald-500/20 text-emerald-300',
  filed: 'bg-emerald-500/30 text-emerald-200',
  nil_selected: 'bg-yellow-500/20 text-yellow-300',
  nil_filed: 'bg-emerald-500/30 text-emerald-200',
  overdue: 'bg-red-500/20 text-red-300',
  cancelled: 'bg-slate-700 text-slate-500',
};

const PAY_STATUS_CLS = {
  not_set: 'bg-slate-700 text-slate-400',
  unpaid: 'bg-red-500/20 text-red-300',
  partly_paid: 'bg-amber-500/20 text-amber-300',
  paid: 'bg-emerald-500/20 text-emerald-300',
  complimentary: 'bg-indigo-500/20 text-indigo-300',
  waived: 'bg-slate-500/20 text-slate-300',
  covered_until_date: 'bg-emerald-500/20 text-emerald-300',
  invoice_issued: 'bg-blue-500/20 text-blue-300',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

// ─── API ─────────────────────────────────────────────────────────────────────
const gstApi = {
  listRegs: (cid) => api.get(`/admin/companies/${cid}/gst-registrations`).then(r => r.data),
  createReg: (cid, body) => api.post(`/admin/companies/${cid}/gst-registrations`, body).then(r => r.data),
  patchReg: (id, body) => api.patch(`/admin/gst-registrations/${id}`, body).then(r => r.data),
  previewConfig: (body) => api.post(`/admin/gst-filing-config/preview`, body).then(r => r.data),
  setVerification: (id, body) => api.post(`/admin/gst-registrations/${id}/verification`, body).then(r => r.data),
  deleteReg: (id) => api.delete(`/admin/gst-registrations/${id}`).then(r => r.data),
  regenerate: (id, count) => api.post(`/admin/gst-registrations/${id}/regenerate-filings?count=${count}`).then(r => r.data),
  enableGstr9: (id, fy) => api.post(`/admin/gst-registrations/${id}/enable-gstr9`, { financial_year: fy }).then(r => r.data),
  enableGstr9c: (id, fy) => api.post(`/admin/gst-registrations/${id}/enable-gstr9c`, { financial_year: fy }).then(r => r.data),
  filings: (id, params) => api.get(`/admin/gst-registrations/${id}/filings`, { params }).then(r => r.data),
  patchFiling: (id, body) => api.patch(`/admin/gst-filings/${id}`, body).then(r => r.data),
  toggleNil: (id, body) => api.post(`/admin/gst-filings/${id}/toggle-nil`, body).then(r => r.data),
  sendTaxReminder: (id) => api.post(`/admin/gst-filings/${id}/send-tax-reminder`).then(r => r.data),
  markTaxPaid: (id) => api.post(`/admin/gst-filings/${id}/mark-tax-paid`).then(r => r.data),
  markFiled: (id, body) => api.post(`/admin/gst-filings/${id}/mark-filed`, body).then(r => r.data),
  uploadGstr1Version: (id, fd) => api.post(`/admin/gst-filings/${id}/gstr1-version`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  sendGstr1Confirmation: (id) => api.post(`/admin/gst-filings/${id}/gstr1-send-confirmation`).then(r => r.data),
  uploadAck: (id, formData) => api.post(`/admin/gst-filings/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  filingChecklist: (id) => api.get(`/admin/gst-filings/${id}/checklist`).then(r => r.data),
  filingBulkUpload: (id, fd) => api.post(`/admin/gst-filings/${id}/documents/bulk`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  requestDocuments: (id, body) => api.post(`/admin/gst-filings/${id}/request-documents`, body).then(r => r.data),
  proceedWithAvailable: (id, body) => api.post(`/admin/gst-filings/${id}/proceed-with-available`, body).then(r => r.data),
  dashboard: (cid) => api.get(`/admin/companies/${cid}/gst-dashboard`).then(r => r.data),
  listAgreements: (regId) => api.get(`/admin/gst-registrations/${regId}/pricing-agreements`).then(r => r.data),
  createAgreement: (regId, body) => api.post(`/admin/gst-registrations/${regId}/pricing-agreements`, body).then(r => r.data),
  patchAgreement: (id, body) => api.patch(`/admin/gst-pricing-agreements/${id}`, body).then(r => r.data),
  listPayments: (agrId) => api.get(`/admin/gst-pricing-agreements/${agrId}/payments`).then(r => r.data),
  recordPayment: (agrId, body) => api.post(`/admin/gst-pricing-agreements/${agrId}/payments`, body).then(r => r.data),
  // Razorpay Payment Link (mirrors the order flow — see routes/payments.py)
  createPaymentLink: (agrId) => api.post(`/admin/gst-pricing-agreements/${agrId}/payment-link`).then(r => r.data),
  fetchPaymentLink: (agrId) => api.get(`/admin/gst-pricing-agreements/${agrId}/payment-link`).then(r => r.data),
  cancelPaymentLink: (agrId) => api.delete(`/admin/gst-pricing-agreements/${agrId}/payment-link`).then(r => r.data),
  // Recurring billing (master agreement + embedded billing periods)
  getMaster: (regId) => api.get(`/admin/gst-registrations/${regId}/master-agreement`).then(r => r.data),
  createMaster: (body) => api.post(`/admin/gst-master-agreements`, body).then(r => r.data),
  generatePeriods: (mid) => api.post(`/admin/gst-master-agreements/${mid}/generate-periods`).then(r => r.data),
  changePlan: (mid, body) => api.post(`/admin/gst-master-agreements/${mid}/change-plan`, body).then(r => r.data),
  periodDiscount: (mid, pid, body) => api.patch(`/admin/gst-master-agreements/${mid}/periods/${pid}`, body).then(r => r.data),
  periodLink: (mid, pid) => api.post(`/admin/gst-master-agreements/${mid}/periods/${pid}/payment-link`).then(r => r.data),
  periodLinkStatus: (mid, pid) => api.get(`/admin/gst-master-agreements/${mid}/periods/${pid}/payment-link`).then(r => r.data),
  periodCancelLink: (mid, pid) => api.delete(`/admin/gst-master-agreements/${mid}/periods/${pid}/payment-link`).then(r => r.data),
  changeScheme: (regId, body) => api.post(`/admin/gst-registrations/${regId}/change-scheme`, body).then(r => r.data),
  periodOfflineSubmit: (mid, pid, body) => api.post(`/admin/gst-master-agreements/${mid}/periods/${pid}/offline-submit`, body).then(r => r.data),
  periodOfflineApprove: (mid, pid, body) => api.post(`/admin/gst-master-agreements/${mid}/periods/${pid}/offline-approve`, body).then(r => r.data),
  periodOfflineReject: (mid, pid, body) => api.post(`/admin/gst-master-agreements/${mid}/periods/${pid}/offline-reject`, body).then(r => r.data),
  periodComplimentary: (mid, pid, body) => api.post(`/admin/gst-master-agreements/${mid}/periods/${pid}/mark-complimentary`, body).then(r => r.data),
  billingPeriods: (status) => api.get(`/admin/gst-compliance/billing-periods`, { params: status ? { status_filter: status } : {} }).then(r => r.data),
};

// ─── Primitives ──────────────────────────────────────────────────────────────
const Field = ({ label, children, required }) => (
  <div>
    <label className="text-[11px] text-slate-400 block mb-1">
      {label}{required && <span className="text-emerald-400 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);
const Input = (p) => (
  <input {...p} className={`w-full h-9 rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${p.className || ''}`} />
);
const Select = (p) => (
  <select {...p} className={`w-full h-9 rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${p.className || ''}`}>{p.children}</select>
);
const Textarea = (p) => (
  <textarea {...p} className={`w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${p.className || ''}`} />
);

const Modal = ({ title, onClose, children, size = 'md', testId }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
    <div
      className={`bg-[#0f1611] border border-emerald-900/40 rounded-2xl w-full max-h-[90vh] overflow-y-auto ${
        size === 'lg' ? 'max-w-3xl' : 'max-w-lg'}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      <div className="sticky top-0 bg-[#0f1611] p-4 border-b border-emerald-900/40 flex items-center justify-between z-10">
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid={`${testId}-close`}>
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

// ─── GST Profile (P1) — shared fields, helpers ───────────────────────────────
const CONSTITUTION_OPTIONS = [
  'Proprietorship', 'Partnership Firm', 'Limited Liability Partnership',
  'Private Limited Company', 'Public Limited Company', 'Hindu Undivided Family',
  'Trust', 'Society / Club / AOP', 'Government Department', 'Other',
];
const TURNOVER_OPTIONS = ['<40L', '40L-1.5Cr', '1.5Cr-5Cr', '5Cr-20Cr', '>20Cr'];
const emptyAddr = () => ({ address_line1: '', address_line2: '', city: '', pincode: '' });

const emptyProfile = () => ({
  trade_name: '', constitution_of_business: '', annual_turnover_range: '',
  nature_of_business: '',
  authorized_signatory_director_id: '', admin_notes: '', client_visible_notes: '',
  ppob: emptyAddr(), additional_places: [],
});

const profileFromReg = (r) => ({
  trade_name: r.trade_name || '',
  constitution_of_business: r.constitution_of_business || '',
  annual_turnover_range: r.annual_turnover_range || '',
  nature_of_business: Array.isArray(r.nature_of_business) ? r.nature_of_business.join(', ') : '',
  authorized_signatory_director_id: r.authorized_signatory_director_id || '',
  admin_notes: r.admin_notes || '',
  client_visible_notes: r.client_visible_notes || '',
  lut_applicable: !!r.lut_applicable,
  ppob: { ...emptyAddr(), ...(r.principal_place_of_business || {}) },
  additional_places: Array.isArray(r.additional_places_of_business)
    ? r.additional_places_of_business.map((a) => ({ ...emptyAddr(), ...a })) : [],
});

const addrHasData = (a) => a && (a.address_line1 || a.address_line2 || a.city || a.pincode);

const buildProfileBody = (p) => ({
  trade_name: p.trade_name.trim() || null,
  constitution_of_business: p.constitution_of_business || null,
  annual_turnover_range: p.annual_turnover_range || null,
  nature_of_business: p.nature_of_business.trim()
    ? p.nature_of_business.split(',').map((s) => s.trim()).filter(Boolean) : null,
  authorized_signatory_director_id: p.authorized_signatory_director_id.trim() || null,
  admin_notes: p.admin_notes.trim() || null,
  client_visible_notes: p.client_visible_notes.trim() || null,
  lut_applicable: !!p.lut_applicable,
  principal_place_of_business: addrHasData(p.ppob) ? p.ppob : null,
  additional_places_of_business:
    p.additional_places.filter(addrHasData).length
      ? p.additional_places.filter(addrHasData) : null,
});

const AddressBlock = ({ addr, onChange, testPrefix }) => (
  <div className="grid grid-cols-2 gap-2">
    <Input value={addr.address_line1} placeholder="Address line 1"
           onChange={(e) => onChange({ ...addr, address_line1: e.target.value })}
           data-testid={`${testPrefix}-line1`} />
    <Input value={addr.address_line2} placeholder="Address line 2"
           onChange={(e) => onChange({ ...addr, address_line2: e.target.value })}
           data-testid={`${testPrefix}-line2`} />
    <Input value={addr.city} placeholder="City"
           onChange={(e) => onChange({ ...addr, city: e.target.value })}
           data-testid={`${testPrefix}-city`} />
    <Input value={addr.pincode} placeholder="Pincode"
           onChange={(e) => onChange({ ...addr, pincode: e.target.value })}
           data-testid={`${testPrefix}-pincode`} />
  </div>
);

// Renders all 11 additive GST-profile fields. `locked` disables the fields that
// become immutable once the registration is verified.
const ProfileFields = ({ p, setP, locked = {} }) => {
  const set = (k, v) => setP({ ...p, [k]: v });
  const setAdditional = (i, v) => {
    const next = [...p.additional_places]; next[i] = v; set('additional_places', next);
  };
  return (
    <div className="space-y-4" data-testid="gst-profile-fields">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Trade name">
          <Input value={p.trade_name} onChange={(e) => set('trade_name', e.target.value)}
                 data-testid="gst-trade-name" />
        </Field>
        <Field label={`Constitution of business${locked.constitution_of_business ? ' (locked)' : ''}`}>
          <Select value={p.constitution_of_business}
                  disabled={!!locked.constitution_of_business}
                  onChange={(e) => set('constitution_of_business', e.target.value)}
                  data-testid="gst-constitution">
            <option value="">— Select —</option>
            {CONSTITUTION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Annual turnover range">
          <Select value={p.annual_turnover_range}
                  onChange={(e) => set('annual_turnover_range', e.target.value)}
                  data-testid="gst-turnover">
            <option value="">— Select —</option>
            {TURNOVER_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Authorized signatory (director ID)">
          <Input value={p.authorized_signatory_director_id}
                 onChange={(e) => set('authorized_signatory_director_id', e.target.value)}
                 placeholder="Director ID"
                 data-testid="gst-signatory" />
        </Field>
      </div>

      <Field label="Nature of business (comma separated)">
        <Input value={p.nature_of_business}
               onChange={(e) => set('nature_of_business', e.target.value)}
               placeholder="e.g. Trading, Manufacturing, Services"
               data-testid="gst-nature" />
      </Field>

      <label className="flex items-center gap-2 rounded-lg border border-emerald-900/30 p-3 bg-slate-900/30 cursor-pointer" data-testid="gst-lut-applicable-wrap">
        <input type="checkbox" checked={!!p.lut_applicable}
               onChange={(e) => set('lut_applicable', e.target.checked)}
               data-testid="gst-lut-applicable" />
        <span className="text-sm text-white">LUT applicable</span>
        <span className="text-[11px] text-slate-500">— generates a LUT record each financial year (admin files ARN + acknowledgement; no client action, no invoice).</span>
      </label>

      <div className="rounded-lg border border-emerald-900/30 p-3 space-y-2 bg-slate-900/30">
        <div className="text-[11px] text-emerald-400 uppercase tracking-wider">Principal place of business</div>
        <AddressBlock addr={p.ppob} onChange={(v) => set('ppob', v)} testPrefix="gst-ppob" />
      </div>

      <div className="rounded-lg border border-emerald-900/30 p-3 space-y-2 bg-slate-900/30">
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-emerald-400 uppercase tracking-wider">Additional places of business</div>
          <button type="button"
                  onClick={() => set('additional_places', [...p.additional_places, emptyAddr()])}
                  data-testid="gst-add-place"
                  className="text-xs text-emerald-300 hover:text-white flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        {p.additional_places.length === 0 && (
          <div className="text-[11px] text-slate-500">No additional places added.</div>
        )}
        {p.additional_places.map((a, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-slate-400">Place #{i + 1}</span>
              <button type="button"
                      onClick={() => set('additional_places', p.additional_places.filter((_, j) => j !== i))}
                      data-testid={`gst-remove-place-${i}`}
                      className="text-[11px] text-red-400 hover:text-red-300">Remove</button>
            </div>
            <AddressBlock addr={a} onChange={(v) => setAdditional(i, v)} testPrefix={`gst-addl-${i}`} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Field label="Client-visible notes">
          <Textarea rows={2} value={p.client_visible_notes}
                    onChange={(e) => set('client_visible_notes', e.target.value)}
                    data-testid="gst-client-notes" />
        </Field>
        <Field label="Admin notes (internal only)">
          <Textarea rows={2} value={p.admin_notes}
                    onChange={(e) => set('admin_notes', e.target.value)}
                    data-testid="gst-admin-notes" />
        </Field>
      </div>
    </div>
  );
};


// ─── Add Registration modal ──────────────────────────────────────────────────
// ─── Filing Configuration (single source of truth) ──────────────────────────
// One coherent section. gstr1_return_type / gstr3b_payment_mode are DERIVED by
// the backend — never edited here. Shows a live, server-authoritative summary.
const TAXPAYER_PROFILE_OPTIONS = [
  ['regular', 'Regular'], ['qrmp', 'QRMP'], ['composition', 'Composition'],
  ['ecommerce_operator', 'E-commerce operator'], ['ecommerce_seller', 'E-commerce seller'],
  ['exporter', 'Exporter'], ['sez_supplier', 'SEZ supplier'], ['other', 'Other'],
];

const FilingConfigSection = ({ cfg, setCfg, disabled = {} }) => {
  const [derived, setDerived] = useState(null);
  const isQrmp = cfg.taxpayer_profile === 'qrmp';
  const isComposition = cfg.taxpayer_profile === 'composition';

  useEffect(() => {
    let alive = true;
    gstApi.previewConfig({
      taxpayer_profile: cfg.taxpayer_profile,
      filing_frequency: cfg.filing_frequency,
      use_iff: isQrmp ? !!cfg.use_iff : null,
    }).then((d) => { if (alive) setDerived(d); }).catch(() => { if (alive) setDerived(null); });
    return () => { alive = false; };
  }, [cfg.taxpayer_profile, cfg.filing_frequency, cfg.use_iff, isQrmp]);

  const onProfile = (v) => {
    const next = { ...cfg, taxpayer_profile: v };
    if (v === 'qrmp' || v === 'composition') next.filing_frequency = 'quarterly';
    if (v !== 'qrmp') next.use_iff = false;
    setCfg(next);
  };

  return (
    <div className="rounded-lg border border-emerald-900/30 p-3 space-y-3 bg-slate-900/30"
         data-testid="gst-filing-config">
      <div className="text-[11px] text-emerald-400 uppercase tracking-wider">Filing Configuration</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Taxpayer profile" required>
          <Select value={cfg.taxpayer_profile} disabled={!!disabled.taxpayer_profile}
                  onChange={(e) => onProfile(e.target.value)} data-testid="gst-profile">
            {TAXPAYER_PROFILE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </Field>
        <Field label={`Filing frequency${(isQrmp || isComposition) ? ' (auto)' : ''}`} required>
          <Select value={cfg.filing_frequency}
                  disabled={isQrmp || isComposition || !!disabled.filing_frequency}
                  onChange={(e) => setCfg({ ...cfg, filing_frequency: e.target.value })}
                  data-testid="gst-frequency">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </Select>
        </Field>
      </div>

      {isQrmp && (
        <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer"
               data-testid="gst-use-iff-label">
          <input type="checkbox" checked={!!cfg.use_iff}
                 disabled={!!disabled.use_iff}
                 onChange={(e) => setCfg({ ...cfg, use_iff: e.target.checked })}
                 data-testid="gst-use-iff" />
          Use IFF for the first two months? <span className="text-slate-500 text-[12px]">(QRMP)</span>
        </label>
      )}

      {derived && (
        <div className="rounded-md border border-emerald-900/30 bg-[#080b09] p-3" data-testid="gst-derived-summary">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1.5">Derived filing summary</div>
          <ul className="space-y-0.5">
            {derived.summary.map((s, i) => (
              <li key={i} className="text-[13px] text-emerald-300 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const AddRegistrationModal = ({ companyId, onClose, onSaved }) => {
  const [f, setF] = useState({
    gstin: '', legal_name: '', state: '', filing_start_month: '',
    taxpayer_profile: 'regular', filing_frequency: 'monthly', use_iff: false,
    export_applies: false, export_under_lut: false,
    ecom_is_operator: false,
    composition_is: false,
  });
  const [p, setP] = useState(emptyProfile());
  const [showProfile, setShowProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const canSave = f.gstin.trim().length === 15 && f.legal_name.trim().length >= 2
    && f.filing_start_month.length === 7 && f.state.trim().length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        gstin: f.gstin.toUpperCase().trim(),
        legal_name: f.legal_name.trim(),
        state: f.state.trim(),
        filing_start_month: f.filing_start_month,
        taxpayer_profile: f.taxpayer_profile,
        filing_frequency: f.filing_frequency,
        use_iff: f.taxpayer_profile === 'qrmp' ? f.use_iff : null,
        export_config: { applies: f.export_applies, under_lut: f.export_under_lut },
        ecommerce_config: { is_operator: f.ecom_is_operator },
        composition_config: { is_composition: f.composition_is },
        ...buildProfileBody(p),
      };
      const res = await gstApi.createReg(companyId, body);
      toast.success(`Registered. Generated ${res.generated_filings_count} filing records.`);
      onSaved?.(res.registration);
      onClose();
    } catch (e) { toast.error(getApiError(e, 'Failed to save')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Add GST registration" onClose={onClose} size="lg" testId="gst-add-modal">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="GSTIN" required>
            <Input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })}
                   placeholder="15-char GSTIN" maxLength={15}
                   data-testid="gst-gstin" />
          </Field>
          <Field label="State" required>
            <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}
                   placeholder="e.g. West Bengal" data-testid="gst-state" />
          </Field>
          <Field label="Legal name" required>
            <Input value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })}
                   data-testid="gst-legal-name" />
          </Field>
          <Field label="Filing start month" required>
            <Input type="month" value={f.filing_start_month}
                   onChange={(e) => setF({ ...f, filing_start_month: e.target.value })}
                   data-testid="gst-start-month" />
          </Field>
        </div>

        <FilingConfigSection cfg={f} setCfg={setF} />

        <div className="rounded-lg border border-emerald-900/30 p-3 space-y-2 bg-slate-900/30">
          <div className="text-[11px] text-emerald-400 uppercase tracking-wider">Export</div>
          <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
            <input type="checkbox" checked={f.export_applies}
                   onChange={(e) => setF({ ...f, export_applies: e.target.checked })}
                   data-testid="gst-export-applies" />
            Company does exports
          </label>
          {f.export_applies && (
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer pl-6">
              <input type="checkbox" checked={f.export_under_lut}
                     onChange={(e) => setF({ ...f, export_under_lut: e.target.checked })}
                     data-testid="gst-export-lut" />
              Under LUT (auto-create LUT record)
            </label>
          )}
        </div>

        <div className="rounded-lg border border-emerald-900/30 bg-slate-900/30">
          <button type="button" onClick={() => setShowProfile((v) => !v)}
                  data-testid="gst-toggle-profile"
                  className="w-full flex items-center justify-between p-3 text-sm text-emerald-300 hover:text-white">
            <span className="uppercase tracking-wider text-[11px]">GST Profile Details (optional)</span>
            <ChevronRight className={`w-4 h-4 transition-transform ${showProfile ? 'rotate-90' : ''}`} />
          </button>
          {showProfile && (
            <div className="p-3 pt-0 border-t border-emerald-900/30">
              <ProfileFields p={p} setP={setP} />
            </div>
          )}
        </div>

        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button disabled={!canSave || saving} onClick={save}
                  data-testid="gst-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save & generate
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Edit GST Profile modal (P1) ─────────────────────────────────────────────
const EditProfileModal = ({ registration, onClose, onSaved, onFilingsChanged }) => {
  const [p, setP] = useState(profileFromReg(registration));
  const [cfg, setCfg] = useState({
    taxpayer_profile: registration.taxpayer_profile || 'regular',
    filing_frequency: registration.filing_frequency || 'monthly',
    use_iff: registration.gstr1_return_type === 'iff_qrmp',
  });
  const [saving, setSaving] = useState(false);
  const [showScheme, setShowScheme] = useState(false);
  const verified = registration.verification_status === 'verified';
  const locked = verified ? { constitution_of_business: true } : {};
  // After verification, filing configuration is change-request-only.
  const cfgDisabled = verified
    ? { taxpayer_profile: true, filing_frequency: true, use_iff: true } : {};

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...buildProfileBody(p) };
      if (!verified) {
        body.taxpayer_profile = cfg.taxpayer_profile;
        body.filing_frequency = cfg.filing_frequency;
        body.use_iff = cfg.taxpayer_profile === 'qrmp' ? cfg.use_iff : null;
      }
      await gstApi.patchReg(registration.id, body);
      toast.success('GST profile updated');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(getApiError(e, 'Failed to update profile')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Edit GST profile" onClose={onClose} size="lg" testId="gst-edit-profile-modal">
      <div className="space-y-4">
        {verified && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-start justify-between gap-3"
               data-testid="gst-verified-notice">
            <p className="text-[12px] text-amber-300">
              This registration is verified. Constitution is locked, and the filing
              configuration (monthly ↔ quarterly, profile, IFF) changes through the
              scheme-change workflow so past filings stay intact.
            </p>
            <button onClick={() => setShowScheme(true)} data-testid="gst-change-scheme-btn"
                    className="flex-shrink-0 text-[12px] font-medium text-amber-200 hover:text-white border border-amber-400/50 rounded px-2 py-1 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Change scheme
            </button>
          </div>
        )}
        <FilingConfigSection cfg={cfg} setCfg={setCfg} disabled={cfgDisabled} />
        <ProfileFields p={p} setP={setP} locked={locked} />
        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button disabled={saving} onClick={save}
                  data-testid="gst-edit-profile-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save profile
          </button>
        </div>
      </div>
      {showScheme && (
        <ChangeSchemeModal registration={registration}
          onClose={() => setShowScheme(false)}
          onSaved={() => { setShowScheme(false); onFilingsChanged?.(); onSaved?.(); onClose(); }} />
      )}
    </Modal>
  );
};

const ChangeSchemeModal = ({ registration, onClose, onSaved }) => {
  const isQrmp = (tp) => tp === 'qrmp';
  const isComp = (tp) => tp === 'composition';
  const [f, setF] = useState({
    taxpayer_profile: registration.taxpayer_profile || 'regular',
    filing_frequency: registration.filing_frequency || 'monthly',
    use_iff: registration.gstr1_return_type === 'iff_qrmp',
    effective_from: '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const setProfile = (v) => {
    const next = { ...f, taxpayer_profile: v };
    if (v === 'qrmp' || v === 'composition') next.filing_frequency = 'quarterly';
    if (v !== 'qrmp') next.use_iff = false;
    setF(next);
  };
  const save = async () => {
    if (!f.effective_from) { toast.error('Choose the effective month'); return; }
    if ((f.reason || '').trim().length < 3) { toast.error('Reason (min 3 chars) is required'); return; }
    setSaving(true);
    try {
      const r = await gstApi.changeScheme(registration.id, {
        taxpayer_profile: f.taxpayer_profile,
        filing_frequency: f.filing_frequency,
        use_iff: isQrmp(f.taxpayer_profile) ? f.use_iff : null,
        effective_from: f.effective_from,
        reason: f.reason.trim(),
      });
      toast.success(`Scheme updated — ${r.removed_filings} future filing(s) replaced with ${r.generated_filings} new one(s), effective ${r.effective_from}`);
      onSaved?.();
    } catch (e) { toast.error(getApiError(e, 'Failed to change scheme')); } finally { setSaving(false); }
  };
  return (
    <Modal title="Change filing scheme" onClose={onClose} testId="gst-change-scheme-modal">
      <div className="space-y-3">
        <p className="text-[11px] text-slate-400">
          Applies from the effective month forward. Filed and in-progress filings are kept; future unstarted filings are regenerated on the new cadence. Audited.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Taxpayer profile" required>
            <Select value={f.taxpayer_profile} onChange={(e) => setProfile(e.target.value)} data-testid="gst-scheme-profile">
              <option value="regular">Regular</option>
              <option value="qrmp">QRMP</option>
              <option value="composition">Composition</option>
              <option value="ecommerce_operator">E-commerce operator</option>
              <option value="exporter">Exporter / SEZ</option>
            </Select>
          </Field>
          <Field label="Filing frequency" required>
            <Select value={f.filing_frequency} disabled={isQrmp(f.taxpayer_profile) || isComp(f.taxpayer_profile)}
                    onChange={(e) => setF({ ...f, filing_frequency: e.target.value })} data-testid="gst-scheme-frequency">
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </Select>
          </Field>
        </div>
        {isQrmp(f.taxpayer_profile) && (
          <label className="flex items-center gap-2 text-[12px] text-slate-300" data-testid="gst-scheme-iff">
            <input type="checkbox" checked={!!f.use_iff} onChange={(e) => setF({ ...f, use_iff: e.target.checked })} />
            Opt in to IFF (upload B2B invoices in months 1 &amp; 2)
          </label>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Effective from (month)" required>
            <Input type="month" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} data-testid="gst-scheme-effective" />
          </Field>
          <Field label="Reason" required>
            <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. turnover crossed ₹5cr" data-testid="gst-scheme-reason" />
          </Field>
        </div>
        <div className="pt-2 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button disabled={saving} onClick={save} data-testid="gst-scheme-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Apply scheme change
          </button>
        </div>
      </div>
    </Modal>
  );
};


// ─── Filing detail (Nil toggle + status) ─────────────────────────────────────
const ACK_TYPES = [
  { key: 'gstr1_acknowledgement', label: 'GSTR-1 acknowledgement', forms: ['GSTR-1'] },
  { key: 'gstr3b_acknowledgement', label: 'GSTR-3B acknowledgement', forms: ['GSTR-3B'] },
  { key: 'gstr2a_report', label: 'GSTR-2A report', forms: ['GSTR-1', 'GSTR-3B'] },
  { key: 'tax_payment_challan', label: 'Tax payment challan', forms: ['GSTR-3B'] },
  { key: 'lut_acknowledgement', label: 'LUT acknowledgement', forms: ['LUT'] },
  { key: 'acknowledgement', label: 'Acknowledgement', forms: ['CMP-08', 'GSTR-4', 'GSTR-8', 'GSTR-9', 'GSTR-9C', 'IFF', 'PMT-06'] },
];

// Post-filing: mark filed + upload acknowledgement documents (admin).
const PostFilingSection = ({ filing, onChanged }) => {
  const [arn, setArn] = useState(filing.arn || '');
  const [challan, setChallan] = useState(!!filing.challan_applicable);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [docs, setDocs] = useState(filing.documents || []);
  const [status, setStatus] = useState(filing.status);
  const isNil = !!filing.nil_filing?.selected;
  const isFiled = ['filed', 'filed_acknowledgement_pending', 'filed_complete', 'nil_filed'].includes(status);
  const ackDocs = docs.filter((d) => d.document_type);

  const markFiled = async () => {
    if (!isNil && !arn.trim()) { toast.error('ARN is required to mark as filed'); return; }
    setBusy(true);
    try {
      const r = await gstApi.markFiled(filing.id, { arn: arn.trim() || undefined, challan_applicable: challan });
      setStatus(r.status);
      toast.success(r.status === 'nil_filed' ? 'NIL return filed'
        : r.status === 'filed_complete' ? 'Filed & complete' : 'Filed — acknowledgements pending');
      onChanged?.();
    } catch (e) { toast.error(getApiError(e, 'Failed to mark filed')); }
    finally { setBusy(false); }
  };

  const upload = async (docType, file) => {
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      fd.append('file', file);
      fd.append('document_name', file.name);
      const r = await gstApi.uploadAck(filing.id, fd);
      setDocs(r.documents || []);
      setStatus(r.status);
      toast.success(r.duplicate ? 'Already uploaded' : 'Acknowledgement uploaded');
      onChanged?.();
    } catch (e) { toast.error(getApiError(e, 'Upload failed')); }
    finally { setUploading(null); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/admin/gst-filings/${filing.id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) { toast.error('Could not open document'); }
  };

  const isReturn = filing.form_type === 'GSTR-1' || filing.form_type === 'GSTR-3B';
  return (
    <div className="rounded-lg border border-emerald-900/30 p-3 bg-slate-900/30 space-y-3" data-testid="gst-postfiling">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-emerald-400">Post-filing</span>
        <span className={`text-[11px] px-2 py-0.5 rounded ${gstStatusMeta(status).cls}`} title={gstStatusMeta(status).description}>{gstStatusMeta(status).label}</span>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <div className="text-[11px] text-slate-500 mb-1">ARN{isNil ? ' (optional for NIL)' : ''}</div>
          <input value={arn} onChange={(e) => setArn(e.target.value)} placeholder={isNil ? 'Filing ARN (optional)' : 'Filing ARN'}
                 data-testid="gst-arn-input"
                 className="w-full bg-[#080b09] border border-emerald-900/40 rounded px-2 py-1.5 text-sm text-white" />
        </div>
        {isReturn && (
          <label className="flex items-center gap-1.5 text-[12px] text-slate-300 pb-2 whitespace-nowrap">
            <input type="checkbox" checked={challan} onChange={(e) => setChallan(e.target.checked)} data-testid="gst-challan-applicable" />
            Challan applicable
          </label>
        )}
        <button onClick={markFiled} disabled={busy} data-testid="gst-mark-filed"
                className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {isNil ? 'Mark NIL filed' : 'Mark filed'}
        </button>
      </div>

      {isFiled && (
        <div className="space-y-1.5">
          <div className="text-[11px] text-slate-500">Acknowledgement documents</div>
          {ACK_TYPES.filter((t) => t.forms.includes(filing.form_type)).map(({ key, label }) => {
            const existing = ackDocs.filter((d) => d.document_type === key);
            return (
              <div key={key} className="flex items-center justify-between gap-2 py-1 border-b border-emerald-900/20">
                <div className="min-w-0">
                  <span className="text-[12px] text-white">{label}</span>
                  {existing.map((d) => (
                    <button key={d.id} onClick={() => download(d)} data-testid={`gst-ack-download-${d.id}`}
                            className="ml-2 text-[11px] text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> view
                    </button>
                  ))}
                </div>
                <label className="text-[11px] px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 cursor-pointer flex items-center gap-1 whitespace-nowrap"
                       data-testid={`gst-ack-upload-${key}`}>
                  {uploading === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                         onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(key, f); e.target.value = ''; }} />
                </label>
              </div>
            );
          })}
          <p className="text-[11px] text-slate-500 pt-1">
            GSTR-1/3B become <span className="text-emerald-400">filed_complete</span> only after the required acknowledgement + GSTR-2A (and challan, if applicable) are uploaded.
          </p>
        </div>
      )}
    </div>
  );
};

const GSTR1_WF_STATUS = {
  admin_preparing: { cls: 'bg-blue-500/20 text-blue-300', label: 'Admin preparing' },
  client_confirmation_pending: { cls: 'bg-amber-500/20 text-amber-300', label: 'Client confirmation pending' },
  client_changes_requested: { cls: 'bg-red-500/20 text-red-300', label: 'Changes requested' },
  admin_resubmitted: { cls: 'bg-blue-500/20 text-blue-300', label: 'Admin resubmitted' },
  ready_for_filing: { cls: 'bg-emerald-500/20 text-emerald-300', label: 'Ready for filing' },
};

// GSTR-1 Excel version-confirmation panel (admin). GSTR-1 only.
const Gstr1VersionPanel = ({ filing, onChanged }) => {
  const [versions, setVersions] = useState(filing.gstr1_versions || []);
  const [status, setStatus] = useState(filing.status);
  const [busy, setBusy] = useState(false);
  const st = GSTR1_WF_STATUS[status];

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await gstApi.uploadGstr1Version(filing.id, fd);
      setVersions(r.gstr1_versions || []); setStatus(r.status);
      toast.success(r.message); onChanged?.();
    } catch (e) { toast.error(getApiError(e, 'Upload failed')); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { const r = await gstApi.sendGstr1Confirmation(filing.id); setStatus(r.status); toast.success('Sent to client'); onChanged?.(); }
    catch (e) { toast.error(getApiError(e, 'Failed to send')); } finally { setBusy(false); }
  };
  const download = async (v) => {
    try {
      const res = await api.get(`/admin/gst-filings/${filing.id}/gstr1-version/${v.document_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: v.mime_type || 'application/octet-stream' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch { toast.error('Could not open file'); }
  };
  const canSend = versions.length > 0 && status !== 'client_confirmation_pending' && status !== 'ready_for_filing';

  return (
    <div className="rounded-lg border border-emerald-900/30 p-3 bg-slate-900/30 space-y-3" data-testid="gstr1-version-panel">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-emerald-400">{filing.form_type} version confirmation</span>
        {st && <span className={`text-[11px] px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>}
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[12px] px-2.5 py-1.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 cursor-pointer flex items-center gap-1"
               data-testid="gstr1-upload-version">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {versions.length === 0 ? 'Upload Version 1' : `Upload Version ${versions.length + 1}`}
          <input type="file" className="hidden" accept=".xlsx,.xls,.csv,.pdf" disabled={busy}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </label>
        <button onClick={send} disabled={!canSend || busy} data-testid="gstr1-send-confirmation"
                className="text-[12px] px-2.5 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-40 flex items-center gap-1">
          <Send className="w-3.5 h-3.5" /> Send for confirmation
        </button>
      </div>
      {versions.length > 0 && (
        <div className="space-y-1">
          {versions.map((v) => (
            <div key={v.version_number} className="flex items-center justify-between text-[12px] py-1 border-b border-emerald-900/20" data-testid={`gstr1-version-${v.version_number}`}>
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${v.is_current ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700/40 text-slate-400'}`}>V{v.version_number}{v.is_current ? ' · current' : ''}</span>
                <button onClick={() => download(v)} className="text-emerald-300 hover:text-white inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {v.file_name || 'file'}</button>
              </div>
              <div className="text-right">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${v.client_decision === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : v.client_decision === 'changes_requested' ? 'bg-red-500/20 text-red-300' : 'bg-slate-700/40 text-slate-400'}`}>{v.client_decision}</span>
                {v.client_remark && <div className="text-[10px] text-slate-400 mt-0.5 max-w-[220px] truncate">“{v.client_remark}”</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// GSTR-3B tax-payment panel (admin): send reminder → mark tax paid.
const TaxPaymentPanel = ({ filing, onChanged }) => {
  const [status, setStatus] = useState(filing.status);
  const [busy, setBusy] = useState(false);
  const paid = status === 'tax_paid' || ['filed', 'filed_acknowledgement_pending', 'filed_complete', 'complete'].includes(status);
  const pending = status === 'tax_reminder_sent' || status === 'tax_pending';
  const remind = async () => {
    setBusy(true);
    try { const r = await gstApi.sendTaxReminder(filing.id); setStatus(r.status); toast.success('Tax reminder sent to client'); onChanged?.(); }
    catch (e) { toast.error(getApiError(e, 'Failed')); } finally { setBusy(false); }
  };
  const markPaid = async () => {
    setBusy(true);
    try { const r = await gstApi.markTaxPaid(filing.id); setStatus(r.status); toast.success('Tax marked paid'); onChanged?.(); }
    catch (e) { toast.error(getApiError(e, 'Failed')); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-amber-900/30 p-3 bg-amber-500/5 space-y-2" data-testid="gst-tax-payment-panel">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-amber-300">Tax payment (GSTR-3B)</span>
        <span className={`text-[11px] px-2 py-0.5 rounded ${paid ? 'bg-emerald-500/20 text-emerald-300' : pending ? 'bg-blue-500/20 text-blue-300' : 'bg-amber-500/20 text-amber-300'}`}>
          {paid ? 'Tax paid' : pending ? 'Tax reminder sent' : 'Tax payment due'}
        </span>
      </div>
      <p className="text-[11px] text-slate-400">Client pays tax on the GST portal. Send a reminder, then mark it paid once done — then file the return.</p>
      <div className="flex gap-2">
        <button onClick={remind} disabled={busy || paid} data-testid="gst-send-tax-reminder"
                className="text-[12px] px-2 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40 flex items-center gap-1">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send tax reminder
        </button>
        <button onClick={markPaid} disabled={busy || paid} data-testid="gst-mark-tax-paid"
                className="text-[12px] px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Mark tax paid
        </button>
      </div>
    </div>
  );
};


const FilingDetailModal = ({ filing, onClose, onChanged }) => {
  const [nilSelected, setNilSelected] = useState(!!filing.nil_filing?.selected);
  const [reason, setReason] = useState(filing.nil_filing?.reason || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await gstApi.toggleNil(filing.id, { selected: nilSelected, reason });
      toast.success(nilSelected ? 'Marked as Nil' : 'Nil removed');
      onChanged?.();
      onClose();
    } catch (e) { toast.error(getApiError(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`${filing.filing_period} · ${filing.form_type}`}
           onClose={onClose} testId="gst-filing-modal">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-[11px] text-slate-500">Filing period</div><div className="text-white">{filing.filing_period}</div></div>
          <div><div className="text-[11px] text-slate-500">Filing month</div><div className="text-white">{filing.filing_month || '—'}</div></div>
          <div><div className="text-[11px] text-slate-500">Due date</div><div className="text-white">{fmtDate(filing.due_date)}</div></div>
          <div><div className="text-[11px] text-slate-500">Status</div><span className={`text-[11px] px-2 py-0.5 rounded ${gstStatusMeta(filing.status).cls}`} title={gstStatusMeta(filing.status).description}>{gstStatusMeta(filing.status).label}</span></div>
        </div>

        <div className="rounded-lg border border-emerald-900/30 p-3 bg-slate-900/30">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={nilSelected}
                   onChange={(e) => setNilSelected(e.target.checked)}
                   data-testid="gst-nil-toggle" />
            <span className="text-sm text-white">Nil filing</span>
          </label>
          {nilSelected && (
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Reason (optional)" className="mt-2"
                      data-testid="gst-nil-reason" />
          )}
          {filing.nil_filing?.selected && (
            <p className="text-[11px] text-emerald-300 mt-2" data-testid="gst-nil-audit">
              Marked NIL by {filing.nil_filing.selected_by_name || 'Unknown'}
              {filing.nil_filing.selected_by_role ? ` (${filing.nil_filing.selected_by_role})` : ''}
              {filing.nil_filing.selected_at ? ` on ${fmtDate(filing.nil_filing.selected_at)}` : ''}
            </p>
          )}
          <p className="text-[11px] text-slate-500 mt-2">
            Marking NIL sends a confirmation request to the client. Once the client confirms (or immediately, if needed),
            file it below — a NIL return files with ARN optional. It is <span className="text-emerald-400">never</span> marked as Not required.
          </p>
        </div>

        {filing.form_type === 'GSTR-3B' && <TaxPaymentPanel filing={filing} onChanged={onChanged} />}
        <SourceDocsPanel filing={filing} onChanged={onChanged} />
        <PostFilingSection filing={filing} onChanged={onChanged} />
        {(filing.form_type === 'GSTR-1' || filing.form_type === 'CMP-08') && <Gstr1VersionPanel filing={filing} onChanged={onChanged} />}

        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving}
                  data-testid="gst-nil-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

const DOCSTATUS_CLS = {
  documents_pending: 'bg-slate-600 text-slate-200', partially_uploaded: 'bg-amber-500/20 text-amber-300',
  under_review: 'bg-blue-500/20 text-blue-300', documents_required: 'bg-red-500/20 text-red-300',
  documents_approved: 'bg-emerald-500/20 text-emerald-300', overdue: 'bg-red-500/30 text-red-200',
};
const SourceDocsPanel = ({ filing, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setData(await gstApi.filingChecklist(filing.id)); } catch (e) { /* silent */ }
  }, [filing.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (code, files) => {
    const list = Array.from(files || []); if (!list.length) return;
    setBusy(true);
    try { const fd = new FormData(); list.forEach((f) => fd.append('files', f)); fd.append('document_type', code);
      const r = await gstApi.filingBulkUpload(filing.id, fd);
      toast.success(`${r.uploaded} uploaded${(r.duplicates || []).length ? `, ${r.duplicates.length} dup skipped` : ''}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(getApiError(e, 'Upload failed')); } finally { setBusy(false); }
  };
  const requestDocs = async () => {
    const code = window.prompt('Document code to request (e.g. bank_statement):'); if (!code) return;
    const remark = window.prompt('Remark to client (mandatory, min 3 chars):');
    if (!remark || remark.trim().length < 3) { toast.error('Remark of 3+ chars required'); return; }
    setBusy(true);
    try { await gstApi.requestDocuments(filing.id, { document_types: [{ code, label: code.replace(/_/g, ' ') }], remark }); toast.success('Requested — client notified'); await load(); onChanged?.(); }
    catch (e) { toast.error(getApiError(e, 'Request failed')); } finally { setBusy(false); }
  };
  const proceed = async () => {
    const remark = window.prompt('Proceed with available documents — remark (optional):') || undefined;
    setBusy(true);
    try { await gstApi.proceedWithAvailable(filing.id, { remark }); toast.success('Proceeding with available documents'); await load(); onChanged?.(); }
    catch (e) { toast.error(getApiError(e, 'Failed')); } finally { setBusy(false); }
  };
  const download = async (doc) => {
    try {
      const res = await api.get(`/admin/gst-filings/${filing.id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) { toast.error('Could not open document'); }
  };
  if (!data) return null;
  return (
    <div className="rounded-lg border border-emerald-900/30 p-3 bg-slate-900/30" data-testid="gst-source-docs">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-emerald-300 uppercase tracking-wider">Source documents</h4>
        <span className={`text-[10px] px-2 py-0.5 rounded ${DOCSTATUS_CLS[data.document_status] || 'bg-slate-700'}`} data-testid="gst-doc-status">{data.document_status.replace(/_/g, ' ')}</span>
      </div>
      {data.missing.length > 0 && <p className="text-[11px] text-amber-300 mb-2" data-testid="gst-missing-docs">Missing: {data.missing.map((m) => m.label).join(', ')}</p>}
      <div className="space-y-1.5">
        {data.checklist.map((c) => {
          const files = (data.documents || []).filter((d) => d.document_type === c.code);
          return (
            <div key={c.code} className="text-[12px]">
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-300">{c.label}{c.required ? ' *' : ''}{c.requested_later ? ' (requested)' : ''} <span className="text-slate-500">({files.length})</span></span>
                <label className="cursor-pointer text-emerald-300 hover:text-emerald-200 flex items-center gap-1" data-testid={`gst-upload-${c.code}`}>
                  <Upload className="w-3 h-3" /> Upload
                  <input type="file" multiple className="hidden" disabled={busy} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                         onChange={(e) => { if (e.target.files?.length) upload(c.code, e.target.files); e.target.value = ''; }} />
                </label>
              </div>
              {files.length > 0 && (
                <div className="mt-1 ml-2 flex flex-col gap-1">
                  {files.map((d) => (
                    <button key={d.id} onClick={() => download(d)} data-testid={`gst-src-download-${d.id}`}
                            className="inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200 text-left">
                      <Download className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate max-w-[220px]">{d.document_name || d.file_name || 'document'}</span>
                      {d.uploaded_by_role === 'client' && <span className="text-[9px] text-slate-500">· client</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Files uploaded without a matching checklist tag (e.g. legacy "other") */}
      {(() => {
        const codes = new Set((data.checklist || []).map((c) => c.code));
        const untagged = (data.documents || []).filter((d) => !codes.has(d.document_type));
        if (!untagged.length) return null;
        return (
          <div className="mt-2 pt-2 border-t border-emerald-900/30">
            <span className="text-[11px] text-slate-400">Other uploads <span className="text-slate-500">({untagged.length})</span></span>
            <div className="mt-1 ml-2 flex flex-col gap-1">
              {untagged.map((d) => (
                <button key={d.id} onClick={() => download(d)} data-testid={`gst-src-download-${d.id}`}
                        className="inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200 text-left">
                  <Download className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate max-w-[220px]">{d.document_name || d.file_name || 'document'}</span>
                  {d.uploaded_by_role === 'client' && <span className="text-[9px] text-slate-500">· client</span>}
                </button>
              ))}
            </div>
          </div>
        );
      })()}
      <div className="flex gap-2 mt-3 pt-2 border-t border-emerald-900/30">
        <button onClick={requestDocs} disabled={busy} data-testid="gst-request-docs" className="text-[12px] text-amber-300 hover:text-amber-200">Request documents</button>
        <button onClick={proceed} disabled={busy} data-testid="gst-proceed-available" className="text-[12px] text-blue-300 hover:text-blue-200">Proceed with available</button>
      </div>
    </div>
  );
};

// ─── Recurring billing (master agreement + embedded billing periods) ─────────
const PERIOD_STATE_CLS = {
  paid: 'bg-emerald-500/20 text-emerald-300',
  paid_offline_approved: 'bg-emerald-500/20 text-emerald-300',
  due: 'bg-blue-500/20 text-blue-300',
  overdue: 'bg-amber-500/20 text-amber-300',
  payment_hold: 'bg-red-500/20 text-red-300',
  offline_pending_approval: 'bg-purple-500/20 text-purple-300',
  complimentary: 'bg-indigo-500/20 text-indigo-300',
  complimentary_active: 'bg-indigo-500/20 text-indigo-300',
};
const RecurringBillingPanel = ({ registration }) => {
  const [master, setMaster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [linkPeriod, setLinkPeriod] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setMaster((await gstApi.getMaster(registration.id)).master); }
    catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [registration.id]);
  useEffect(() => { load(); }, [load]);

  const genNow = async () => { setBusy('gen'); try { await gstApi.generatePeriods(master.id); toast.success('Periods refreshed'); await load(); } catch (e) { toast.error(getApiError(e, 'Failed')); } finally { setBusy(null); } };
  const editDiscount = async (p) => {
    const v = window.prompt(`Discount % for period ${p.period_start}..${p.period_end} (blank to clear):`, p.discount_value || '');
    if (v === null) return;
    setBusy(p.id);
    try { await gstApi.periodDiscount(master.id, p.id, { discount_type: v ? 'percentage' : null, discount_value: parseFloat(v || '0') }); toast.success('Discount updated'); await load(); }
    catch (e) { toast.error(getApiError(e, 'Failed')); } finally { setBusy(null); }
  };
  const offlineSubmit = async (p) => {
    const ref = window.prompt('Offline payment reference (UTR/cheque no.):', '');
    if (ref === null) return;
    setBusy(p.id);
    try { await gstApi.periodOfflineSubmit(master.id, p.id, { payment_reference: ref, payment_method: 'bank_transfer', remark: 'Submitted by admin' }); toast.success('Submitted for Super Admin approval'); await load(); }
    catch (e) { toast.error(getApiError(e, 'Submit failed')); } finally { setBusy(null); }
  };
  const offlineApprove = async (p) => {
    const remark = window.prompt('Approval remark (min 3 chars, Super Admin only):');
    if (!remark || remark.trim().length < 3) { toast.error('Remark of 3+ chars required'); return; }
    setBusy(p.id);
    try { await gstApi.periodOfflineApprove(master.id, p.id, { remark }); toast.success('Offline payment approved'); await load(); }
    catch (e) { toast.error(getApiError(e, 'Approve failed (Super Admin only)')); } finally { setBusy(null); }
  };
  const offlineReject = async (p) => {
    const remark = window.prompt('Rejection remark (min 3 chars, Super Admin only):');
    if (!remark || remark.trim().length < 3) { toast.error('Remark of 3+ chars required'); return; }
    setBusy(p.id);
    try { await gstApi.periodOfflineReject(master.id, p.id, { remark }); toast.success('Offline payment rejected'); await load(); }
    catch (e) { toast.error(getApiError(e, 'Reject failed (Super Admin only)')); } finally { setBusy(null); }
  };
  const markComplimentary = async (p) => {
    const remark = window.prompt('Complimentary reason (min 3 chars, Super Admin only):');
    if (!remark || remark.trim().length < 3) { toast.error('Reason of 3+ chars required'); return; }
    setBusy(p.id);
    try { await gstApi.periodComplimentary(master.id, p.id, { remark }); toast.success('Marked complimentary'); await load(); }
    catch (e) { toast.error(getApiError(e, 'Failed (Super Admin only)')); } finally { setBusy(null); }
  };

  if (loading) return <div className="mt-3 text-xs text-slate-500" data-testid="gst-recurring-loading">Loading recurring billing…</div>;

  return (
    <div className="mt-3 rounded-lg border border-blue-900/40 p-3 bg-blue-950/10" data-testid="gst-recurring-panel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-blue-300 uppercase tracking-wider flex items-center gap-1.5">
          <RefreshCw className="w-3 h-3" /> Recurring Billing
        </h4>
        {master ? (
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-400 capitalize">{master.pricing_type} · {INR(master.standard_amount)}</span>
            <button onClick={() => setShowChangePlan(true)} data-testid="gst-recurring-change-plan"
                    className="text-[11px] text-amber-300 hover:text-amber-200 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Change plan
            </button>
            <button onClick={genNow} disabled={busy === 'gen'} data-testid="gst-recurring-generate"
                    className="text-[11px] text-blue-300 hover:text-white flex items-center gap-1">
              {busy === 'gen' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Generate now
            </button>
          </div>
        ) : (
          <button onClick={() => setShowCreate(true)} data-testid="gst-recurring-setup"
                  className="text-[11px] font-medium text-blue-300 hover:text-white flex items-center gap-1">
            <Plus className="w-3 h-3" /> Set up recurring billing
          </button>
        )}
      </div>
      {!master && <p className="text-xs text-slate-500 italic">No continuing agreement yet. One master agreement will auto-generate monthly/quarterly/annual periods.</p>}
      {master && (
        <div className="overflow-x-auto rounded border border-blue-900/30">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-900/60 text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="text-left px-2 py-1.5">Period</th>
                <th className="text-right px-2 py-1.5">Amount</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-right px-2 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-900/20">
              {(master.billing_periods || []).map((p) => (
                <tr key={p.id} data-testid={`gst-billing-period-${p.id}`}>
                  <td className="px-2 py-1.5 text-white">{p.period_start} → {p.period_end}</td>
                  <td className="px-2 py-1.5 text-right text-emerald-300">{INR(p.final_amount)}</td>
                  <td className="px-2 py-1.5"><span className={`px-2 py-0.5 rounded ${PERIOD_STATE_CLS[p.payment_state] || 'bg-slate-700'}`}>{(p.payment_state || '').replace(/_/g, ' ')}</span></td>
                  <td className="px-2 py-1.5 text-right">
                    {(p.payment_state === 'paid' || p.payment_state === 'paid_offline_approved') ? (
                      p.invoice_id ? <a href={`/api/invoices/${p.invoice_id}/pdf?token=${localStorage.getItem('token')}`} target="_blank" rel="noopener noreferrer" data-testid={`gst-period-invoice-${p.id}`} className="text-emerald-300 hover:text-emerald-200">Invoice</a> : <span className="text-emerald-400">Paid</span>
                    ) : p.payment_state === 'complimentary_active' ? (
                      <span className="text-indigo-300">Complimentary</span>
                    ) : p.payment_state === 'offline_pending_approval' ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-purple-300">Pending</span>
                        <button onClick={() => offlineApprove(p)} disabled={busy === p.id} data-testid={`gst-period-approve-${p.id}`} className="text-emerald-300 hover:text-emerald-200">Approve</button>
                        <button onClick={() => offlineReject(p)} disabled={busy === p.id} data-testid={`gst-period-reject-${p.id}`} className="text-red-300 hover:text-red-200">Reject</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button onClick={() => editDiscount(p)} disabled={busy === p.id} data-testid={`gst-period-discount-${p.id}`} className="text-amber-300 hover:text-amber-200">Discount</button>
                        <button onClick={() => setLinkPeriod(p)} disabled={busy === p.id} data-testid={`gst-period-link-${p.id}`} className="text-blue-300 hover:text-blue-200">{p.razorpay_payment_link_url ? 'Link ✓' : 'Pay link'}</button>
                        <button onClick={() => offlineSubmit(p)} disabled={busy === p.id} data-testid={`gst-period-offline-submit-${p.id}`} className="text-purple-300 hover:text-purple-200">Offline</button>
                        <button onClick={() => markComplimentary(p)} disabled={busy === p.id} data-testid={`gst-period-comp-${p.id}`} className="text-indigo-300 hover:text-indigo-200">Comp</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showCreate && <CreateMasterModal registration={registration} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {showChangePlan && master && <ChangePlanModal master={master} onClose={() => setShowChangePlan(false)} onSaved={() => { setShowChangePlan(false); load(); }} />}
      {linkPeriod && master && <PeriodPaymentLinkModal master={master} period={linkPeriod} onClose={() => setLinkPeriod(null)} onChange={load} />}
    </div>
  );
};

// ─── Period Razorpay Payment Link modal (mirrors the order/agreement flow) ──
const PeriodPaymentLinkModal = ({ master, period, onClose, onChange }) => {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchExisting = useCallback(async () => {
    setLoading(true);
    try {
      const res = await gstApi.periodLinkStatus(master.id, period.id);
      if (res.has_link) setLink(res);
    } catch { /* no link yet is normal */ } finally { setLoading(false); }
  }, [master.id, period.id]);
  useEffect(() => { fetchExisting(); }, [fetchExisting]);

  useEffect(() => {
    if (!link) return undefined;
    if (link.status !== 'created' && link.status !== 'issued' && link.status !== 'partially_paid') return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await gstApi.periodLinkStatus(master.id, period.id);
        if (res.has_link) { setLink(res); if (res.status === 'paid') { toast.success('Payment confirmed via Razorpay'); onChange?.(); } }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(timer);
  }, [link, master.id, period.id, onChange]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await gstApi.periodLink(master.id, period.id);
      setLink({ has_link: true, ...res });
      toast.success('Payment link ready');
      onChange?.();
    } catch (e) { toast.error(getApiError(e, 'Failed to generate payment link')); }
    finally { setLoading(false); }
  };
  const copy = async () => {
    if (!link?.short_url) return;
    try { await navigator.clipboard.writeText(link.short_url); setCopied(true); toast.success('Payment link copied'); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error('Copy failed — please copy manually'); }
  };
  const refresh = async () => {
    setLoading(true);
    try {
      const res = await gstApi.periodLinkStatus(master.id, period.id);
      if (res.has_link) { setLink(res); if (res.status === 'paid') { toast.success('Payment confirmed'); onChange?.(); } else toast.info(`Status: ${res.status}`); }
    } catch (e) { toast.error(getApiError(e, 'Failed to fetch status')); } finally { setLoading(false); }
  };
  const cancel = async () => {
    if (!link) return;
    if (!window.confirm('Cancel this payment link? A new one can be generated afterwards.')) return;
    setLoading(true);
    try { await gstApi.periodCancelLink(master.id, period.id); setLink(null); toast.success('Payment link cancelled'); onChange?.(); }
    catch (e) { toast.error(getApiError(e, 'Failed to cancel link')); } finally { setLoading(false); }
  };

  const isPaid = link?.status === 'paid' || period.payment_state === 'paid';
  const std = period.standard_amount || 0;
  const hasDisc = (period.discount_value || 0) > 0;

  return (
    <Modal title={`Payment link · ${period.period_start} → ${period.period_end}`} onClose={onClose} testId="gst-period-link-modal">
      <div className="space-y-4">
        <div>
          <div className="text-xs text-slate-400">Amount</div>
          <div className="text-lg font-semibold text-emerald-300" data-testid="gst-period-link-amount">{INR(period.final_amount)}</div>
          {hasDisc && (
            <div className="text-[11px] text-amber-300">
              Standard {INR(std)} · Discount {period.discount_type === 'percentage' ? `${period.discount_value}%` : INR(period.discount_value)}
            </div>
          )}
        </div>

        {isPaid && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2" data-testid="gst-period-link-paid-banner">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-300 font-medium">Paid</span>
          </div>
        )}

        {!link && !loading && !isPaid && (
          <button onClick={generate} data-testid="gst-period-generate-link-btn"
                  className="w-full px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium flex items-center justify-center gap-2">
            <Link2 className="w-4 h-4" /> Generate payment link
          </button>
        )}
        {loading && !link && (
          <div className="flex items-center justify-center py-6 text-slate-400 gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Working…</div>
        )}

        {link && link.short_url && (
          <>
            <div>
              <label className="text-[11px] text-slate-400 block mb-1">Payment link</label>
              <div className="flex items-center gap-2">
                <input readOnly value={link.short_url} data-testid="gst-period-link-url"
                       className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 font-mono" />
                <button onClick={copy} data-testid="gst-period-link-copy" className="px-2 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs flex items-center gap-1">
                  {copied ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Status: <span className="text-slate-300">{link.status}</span></div>
            </div>
            {!isPaid && (
              <div className="flex items-center gap-2">
                <button onClick={refresh} disabled={loading} data-testid="gst-period-link-refresh" className="text-[12px] text-blue-300 hover:text-blue-200 flex items-center gap-1">
                  <RefreshCw className="w-3.5 h-3.5" /> Check status
                </button>
                <button onClick={cancel} disabled={loading} data-testid="gst-period-link-cancel" className="text-[12px] text-red-300 hover:text-red-200">Cancel link</button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

const ChangePlanModal = ({ master, onClose, onSaved }) => {
  const [f, setF] = useState({
    new_pricing_type: master.pricing_type || 'monthly',
    new_standard_amount: master.standard_amount ?? '',
    effective_from: '',
    next_period_amount: '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const cadenceChanged = f.new_pricing_type !== master.pricing_type;
  const amountChanged = String(f.new_standard_amount) !== String(master.standard_amount ?? '');
  const save = async () => {
    if ((f.reason || '').trim().length < 3) { toast.error('Reason (min 3 chars) is required'); return; }
    if (!cadenceChanged && !amountChanged && f.next_period_amount === '') {
      toast.error('Change the cadence, standard amount, or set a next-period price'); return;
    }
    setSaving(true);
    try {
      const body = { reason: f.reason.trim() };
      if (cadenceChanged) body.new_pricing_type = f.new_pricing_type;
      if (f.new_standard_amount !== '') body.new_standard_amount = parseFloat(f.new_standard_amount);
      if (f.effective_from) body.effective_from = f.effective_from;
      if (f.next_period_amount !== '') body.next_period_amount = parseFloat(f.next_period_amount);
      const r = await gstApi.changePlan(master.id, body);
      toast.success(`Plan updated — ${r.removed} future period(s) replaced, effective ${r.effective_from}`);
      onSaved?.();
    } catch (e) { toast.error(getApiError(e, 'Failed to change plan')); } finally { setSaving(false); }
  };
  return (
    <Modal title="Change billing plan" onClose={onClose} testId="gst-change-plan-modal">
      <div className="space-y-3">
        <p className="text-[11px] text-slate-400">
          Paid and pending-approval periods are kept. Unpaid future periods from the effective date are removed and regenerated on the new plan.
        </p>
        <Field label="Billing cadence" required>
          <Select value={f.new_pricing_type} onChange={(e) => setF({ ...f, new_pricing_type: e.target.value })} data-testid="gst-changeplan-cadence">
            <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Yearly</option>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="New standard amount (₹)" required>
            <Input type="number" value={f.new_standard_amount} onChange={(e) => setF({ ...f, new_standard_amount: e.target.value })} data-testid="gst-changeplan-amount" />
          </Field>
          <Field label="Effective from (optional)">
            <Input type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} data-testid="gst-changeplan-effective" />
          </Field>
        </div>
        <Field label="Override next period price (₹, optional)">
          <Input type="number" value={f.next_period_amount} onChange={(e) => setF({ ...f, next_period_amount: e.target.value })}
                 placeholder="Leave blank to use the standard amount" data-testid="gst-changeplan-next-amount" />
        </Field>
        <Field label="Reason" required>
          <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Client upgraded to quarterly plan" data-testid="gst-changeplan-reason" />
        </Field>
        <div className="pt-2 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button disabled={saving} onClick={save} data-testid="gst-changeplan-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Apply change
          </button>
        </div>
      </div>
    </Modal>
  );
};

const CreateMasterModal = ({ registration, onClose, onSaved }) => {
  const [f, setF] = useState({ pricing_type: 'monthly', standard_amount: '', default_discount_type: '', default_discount_value: '', service_start_date: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await gstApi.createMaster({
        gst_registration_id: registration.id, pricing_type: f.pricing_type,
        standard_amount: parseFloat(f.standard_amount || '0'),
        default_discount_type: f.default_discount_type || null,
        default_discount_value: parseFloat(f.default_discount_value || '0'),
        service_start_date: f.service_start_date || `${(registration.filing_start_month || '2026-04')}-01`,
      });
      toast.success('Recurring billing set up');
      onSaved?.();
    } catch (e) { toast.error(getApiError(e, 'Failed to create')); } finally { setSaving(false); }
  };
  return (
    <Modal title="Set up recurring billing" onClose={onClose} testId="gst-create-master-modal">
      <div className="space-y-3">
        <Field label="Billing cadence" required>
          <Select value={f.pricing_type} onChange={(e) => setF({ ...f, pricing_type: e.target.value })} data-testid="gst-master-cadence">
            <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Yearly</option>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Standard amount (₹)" required>
            <Input type="number" value={f.standard_amount} onChange={(e) => setF({ ...f, standard_amount: e.target.value })} data-testid="gst-master-amount" />
          </Field>
          <Field label="Service start date">
            <Input type="date" value={f.service_start_date} onChange={(e) => setF({ ...f, service_start_date: e.target.value })} data-testid="gst-master-start" />
          </Field>
          <Field label="Discount type">
            <Select value={f.default_discount_type} onChange={(e) => setF({ ...f, default_discount_type: e.target.value })} data-testid="gst-master-disc-type">
              <option value="">None</option><option value="percentage">Percentage</option><option value="fixed">Fixed ₹</option>
            </Select>
          </Field>
          <Field label="Discount value">
            <Input type="number" value={f.default_discount_value} onChange={(e) => setF({ ...f, default_discount_value: e.target.value })} data-testid="gst-master-disc-value" />
          </Field>
        </div>
        <div className="pt-2 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button disabled={saving || !f.standard_amount} onClick={save} data-testid="gst-master-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-blue-500 hover:bg-blue-400 text-white disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Create
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Pricing panel per registration ──────────────────────────────────────────
const PricingPanel = ({ registration, onFilingsInvalidate }) => {
  const [agreements, setAgreements] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showPay, setShowPay] = useState(null);
  const [showLink, setShowLink] = useState(null); // agreement object

  const load = useCallback(async () => {
    try { setAgreements(await gstApi.listAgreements(registration.id)); }
    catch (e) { toast.error(getApiError(e, 'Failed to load pricing')); }
  }, [registration.id]);
  useEffect(() => { load(); }, [load]);

  // afterMutation kept for future use when link cancellation/renewal should refresh filings
  // (currently unused directly — modals call `load` on save)

  return (
    <div className="mt-3 rounded-lg border border-emerald-900/30 p-3 bg-slate-900/20"
         data-testid="gst-pricing-panel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
          <IndianRupee className="w-3 h-3" /> Pricing & Payment
        </h4>
        <button onClick={() => setShowCreate(true)}
                data-testid={`gst-add-agreement-${registration.id}`}
                className="text-[11px] font-medium text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
          <Plus className="w-3 h-3" /> New agreement
        </button>
      </div>
      {agreements.length === 0 && (
        <p className="text-xs text-slate-500 italic">No pricing agreement yet.</p>
      )}
      {agreements.map((a) => {
        const canGenerateLink = !a.is_complimentary && a.payment_status !== 'paid' && (a.final_price || 0) > 0;
        return (
          <div key={a.id}
               data-testid={`gst-agreement-${a.id}`}
               className="rounded-md bg-slate-900/60 border border-emerald-900/20 p-2.5 text-xs space-y-1 mb-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-slate-400">Type:</span><span className="text-white font-medium">{a.pricing_type}</span>
              <span className="text-slate-400">Standard:</span><span className="text-slate-200">{INR(a.standard_price)}</span>
              {(a.discount_value || 0) > 0 && (
                <>
                  <span className="text-slate-400">Discount:</span>
                  <span className="text-amber-300">{a.discount_type === 'percentage' ? `${a.discount_value}%` : INR(a.discount_value)}</span>
                </>
              )}
              <span className="text-slate-400">Final:</span><span className="text-emerald-300 font-semibold">{INR(a.final_price)}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded text-[10.5px] ${PAY_STATUS_CLS[a.payment_status] || 'bg-slate-700'}`}>
                {a.payment_status.replace(/_/g, ' ')}
              </span>
              <span className={`px-2 py-0.5 rounded text-[10.5px] ${
                a.service_status === 'active' ? 'bg-emerald-500/20 text-emerald-300' :
                a.service_status === 'expired' ? 'bg-red-500/20 text-red-300' :
                a.service_status === 'expiring_soon' ? 'bg-amber-500/20 text-amber-300' :
                'bg-slate-700 text-slate-400'}`}>
                service: {a.service_status.replace(/_/g, ' ')}
              </span>
              {a.paid_through_date && (
                <span className="text-emerald-400 text-[11px]">Paid until {fmtDate(a.paid_through_date)}</span>
              )}
              {a.is_complimentary && <span className="text-indigo-300 text-[11px]">Complimentary</span>}
              <div className="ml-auto flex items-center gap-3">
                {canGenerateLink && (
                  <button onClick={() => setShowLink(a)}
                          data-testid={`gst-payment-link-${a.id}`}
                          className="text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    {a.razorpay_payment_link_id ? 'View link' : 'Payment link'}
                  </button>
                )}
                <button onClick={() => setShowPay(a)}
                        data-testid={`gst-record-payment-${a.id}`}
                        className="text-[11px] text-emerald-400 hover:text-emerald-300">Record payment →</button>
              </div>
            </div>
          </div>
        );
      })}

      {showCreate && (
        <PricingAgreementModal registration={registration} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />
      )}
      {showPay && (
        <RecordPaymentModal agreement={showPay} onClose={() => setShowPay(null)} onSaved={() => { setShowPay(null); load(); onFilingsInvalidate?.(); }} />
      )}
      {showLink && (
        <GstPaymentLinkModal agreement={showLink} onClose={() => setShowLink(null)} onChange={() => load()} />
      )}
    </div>
  );
};

const PricingAgreementModal = ({ registration, onClose, onSaved }) => {
  const [f, setF] = useState({
    pricing_type: 'annual', standard_price: '', discount_type: '', discount_value: '',
    is_complimentary: false, complimentary_reason: '',
    paid_through_date: '', admin_note: '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        pricing_type: f.pricing_type,
        standard_price: parseFloat(f.standard_price || '0'),
        discount_type: f.discount_type || null,
        discount_value: parseFloat(f.discount_value || '0'),
        is_complimentary: f.is_complimentary,
        complimentary_reason: f.complimentary_reason || null,
        paid_through_date: f.paid_through_date || null,
        admin_note: f.admin_note || null,
      };
      await gstApi.createAgreement(registration.id, body);
      toast.success('Pricing agreement saved');
      onSaved?.();
    } catch (e) { toast.error(getApiError(e, 'Save failed')); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New pricing agreement" onClose={onClose} testId="gst-agreement-modal">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pricing type" required>
            <Select value={f.pricing_type} onChange={(e) => setF({ ...f, pricing_type: e.target.value })}
                    data-testid="gst-agr-type">
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
              <option value="per_filing">Per filing</option>
              <option value="custom_package">Custom package</option>
            </Select>
          </Field>
          <Field label="Standard price (₹)" required>
            <Input type="number" min="0" step="100" value={f.standard_price}
                   onChange={(e) => setF({ ...f, standard_price: e.target.value })}
                   data-testid="gst-agr-standard-price" />
          </Field>
          <Field label="Discount type">
            <Select value={f.discount_type} onChange={(e) => setF({ ...f, discount_type: e.target.value })}
                    data-testid="gst-agr-discount-type">
              <option value="">None</option>
              <option value="amount">Amount</option>
              <option value="percentage">Percentage</option>
            </Select>
          </Field>
          <Field label="Discount value">
            <Input type="number" min="0" step="1" value={f.discount_value}
                   onChange={(e) => setF({ ...f, discount_value: e.target.value })}
                   data-testid="gst-agr-discount-value" />
          </Field>
          <Field label="Paid-through date">
            <Input type="date" value={f.paid_through_date}
                   onChange={(e) => setF({ ...f, paid_through_date: e.target.value })}
                   data-testid="gst-agr-paid-through" />
          </Field>
          <label className="flex items-center gap-2 mt-6 cursor-pointer text-sm text-white">
            <input type="checkbox" checked={f.is_complimentary}
                   onChange={(e) => setF({ ...f, is_complimentary: e.target.checked })}
                   data-testid="gst-agr-complimentary" />
            Mark complimentary
          </label>
        </div>
        <Field label="Admin note (audited)">
          <Textarea rows={2} value={f.admin_note}
                    onChange={(e) => setF({ ...f, admin_note: e.target.value })}
                    data-testid="gst-agr-note" />
        </Field>
        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving}
                  data-testid="gst-agr-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

const RecordPaymentModal = ({ agreement, onClose, onSaved }) => {
  const [f, setF] = useState({
    amount: agreement.final_price || '', payment_method: 'cash',
    payment_reference: '', payment_date: new Date().toISOString().slice(0, 10),
    paid_through_date: '', internal_note: '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await gstApi.recordPayment(agreement.id, {
        amount: parseFloat(f.amount || '0'),
        payment_method: f.payment_method,
        payment_reference: f.payment_reference || null,
        payment_date: f.payment_date,
        paid_through_date: f.paid_through_date || null,
        internal_note: f.internal_note || null,
      });
      toast.success('Payment recorded');
      onSaved?.();
    } catch (e) { toast.error(getApiError(e, 'Save failed')); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Record payment" onClose={onClose} testId="gst-payment-modal">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (₹)" required>
            <Input type="number" min="0" step="1" value={f.amount}
                   onChange={(e) => setF({ ...f, amount: e.target.value })}
                   data-testid="gst-pay-amount" />
          </Field>
          <Field label="Method" required>
            <Select value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })}
                    data-testid="gst-pay-method">
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="online_already_received">Online already received</option>
              <option value="invoice_issued">Invoice issued</option>
              <option value="already_paid">Already paid</option>
              <option value="complimentary">Complimentary</option>
              <option value="waived">Waived</option>
            </Select>
          </Field>
          <Field label="Payment date" required>
            <Input type="date" value={f.payment_date}
                   onChange={(e) => setF({ ...f, payment_date: e.target.value })}
                   data-testid="gst-pay-date" />
          </Field>
          <Field label="Paid-through date">
            <Input type="date" value={f.paid_through_date}
                   onChange={(e) => setF({ ...f, paid_through_date: e.target.value })}
                   data-testid="gst-pay-through" />
          </Field>
          <Field label="Reference">
            <Input value={f.payment_reference}
                   onChange={(e) => setF({ ...f, payment_reference: e.target.value })}
                   placeholder="UTR / receipt / invoice #"
                   data-testid="gst-pay-ref" />
          </Field>
        </div>
        <Field label="Internal note (audited)">
          <Textarea rows={2} value={f.internal_note}
                    onChange={(e) => setF({ ...f, internal_note: e.target.value })}
                    data-testid="gst-pay-note" />
        </Field>
        <p className="text-[11px] text-slate-500">
          This does NOT trigger any client notification or checkout link. Client-facing payment is a separate, admin-initiated action outside this module.
        </p>
        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving}
                  data-testid="gst-pay-save"
                  className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Record
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Razorpay Payment Link modal (mirrors OrderDetailView's link modal) ─────
const GstPaymentLinkModal = ({ agreement, onClose, onChange }) => {
  const [link, setLink] = useState(null); // { id, short_url, status, amount, amount_paid, payment_status }
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Initial fetch — reuses existing link if present, otherwise stays null so
  // the admin explicitly clicks "Generate" (matches order flow).
  const fetchExisting = useCallback(async () => {
    setLoading(true);
    try {
      const res = await gstApi.fetchPaymentLink(agreement.id);
      if (res.has_link) setLink(res);
    } catch (e) {
      // Silent — no link yet is a normal state
    } finally { setLoading(false); }
  }, [agreement.id]);
  useEffect(() => { fetchExisting(); }, [fetchExisting]);

  // Poll while the link is pending — auto-refreshes when the client pays
  useEffect(() => {
    if (!link) return undefined;
    if (link.status !== 'created' && link.status !== 'issued' && link.status !== 'partially_paid') return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await gstApi.fetchPaymentLink(agreement.id);
        if (res.has_link) {
          setLink(res);
          if (res.status === 'paid') {
            toast.success('Payment confirmed via Razorpay');
            onChange?.();
          }
        }
      } catch { /* ignore transient errors */ }
    }, 5000);
    return () => clearInterval(timer);
  }, [link, agreement.id, onChange]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await gstApi.createPaymentLink(agreement.id);
      if (res.status === 'already_paid') {
        toast.info('Agreement is already fully paid');
        onChange?.();
        onClose();
        return;
      }
      setLink(res);
      toast.success('Payment link ready');
      onChange?.();
    } catch (e) {
      toast.error(getApiError(e, 'Failed to generate payment link'));
    } finally { setLoading(false); }
  };

  const copy = async () => {
    if (!link?.short_url) return;
    try {
      await navigator.clipboard.writeText(link.short_url);
      setCopied(true);
      toast.success('Payment link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await gstApi.fetchPaymentLink(agreement.id);
      if (res.has_link) {
        setLink(res);
        if (res.status === 'paid') {
          toast.success('Payment confirmed via Razorpay');
          onChange?.();
        } else {
          toast.info(`Status: ${res.status}`);
        }
      }
    } catch (e) {
      toast.error(getApiError(e, 'Failed to fetch status'));
    } finally { setLoading(false); }
  };

  const cancel = async () => {
    if (!link) return;
    if (!window.confirm('Cancel this payment link? A new one can be generated afterwards.')) return;
    setLoading(true);
    try {
      await gstApi.cancelPaymentLink(agreement.id);
      setLink(null);
      toast.success('Payment link cancelled');
      onChange?.();
    } catch (e) {
      toast.error(getApiError(e, 'Failed to cancel link'));
    } finally { setLoading(false); }
  };

  const isPaid = link?.status === 'paid' || link?.payment_status === 'paid';

  return (
    <Modal title="Razorpay Payment Link" onClose={onClose} testId="gst-payment-link-modal">
      <div className="space-y-4">
        <div className="text-sm text-slate-300">
          <div className="text-xs text-slate-400">Amount</div>
          <div className="text-lg font-semibold text-emerald-300">{INR(agreement.final_price)}</div>
          {(agreement.discount_value || 0) > 0 && (
            <div className="text-[11px] text-amber-300">
              Standard {INR(agreement.standard_price)} · Discount {agreement.discount_type === 'percentage' ? `${agreement.discount_value}%` : INR(agreement.discount_value)}
            </div>
          )}
        </div>

        {isPaid && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2"
               data-testid="gst-payment-link-paid-banner">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-300 font-medium">Paid</span>
          </div>
        )}

        {!link && !loading && (
          <button onClick={generate}
                  data-testid="gst-generate-payment-link-btn"
                  className="w-full px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium flex items-center justify-center gap-2">
            <Link2 className="w-4 h-4" /> Generate payment link
          </button>
        )}

        {loading && !link && (
          <div className="flex items-center justify-center py-6 text-slate-400 gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Working…
          </div>
        )}

        {link && link.short_url && (
          <>
            <div>
              <label className="text-[11px] text-slate-400 block mb-1">Payment link</label>
              <div className="flex items-center gap-2">
                <input readOnly value={link.short_url}
                       data-testid="gst-payment-link-url"
                       className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 font-mono" />
                <button onClick={copy}
                        data-testid="gst-copy-payment-link-btn"
                        className="px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-400 text-white text-xs font-medium flex items-center gap-1">
                  {copied ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-slate-400">Status:</span>
              <span data-testid="gst-payment-link-status"
                    className={`px-2 py-0.5 rounded ${
                      link.status === 'paid' ? 'bg-emerald-500/20 text-emerald-300' :
                      link.status === 'partially_paid' ? 'bg-amber-500/20 text-amber-300' :
                      link.status === 'cancelled' || link.status === 'expired' ? 'bg-red-500/20 text-red-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>
                {link.status}
              </span>
              {typeof link.amount_paid === 'number' && link.amount_paid > 0 && (
                <span className="text-slate-400">Paid: <span className="text-emerald-300">{INR(link.amount_paid)}</span></span>
              )}
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
              <button onClick={refresh} disabled={loading}
                      className="text-xs text-slate-300 hover:text-white flex items-center gap-1 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh status
              </button>
              {!isPaid && (
                <button onClick={cancel} disabled={loading}
                        data-testid="gst-cancel-payment-link-btn"
                        className="ml-auto text-xs text-red-400 hover:text-red-300 flex items-center gap-1 disabled:opacity-50">
                  Cancel link
                </button>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end pt-3 border-t border-slate-700">
          <button onClick={onClose}
                  className="px-4 py-1.5 rounded text-sm text-slate-300 hover:text-white">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};



// ─── Enable annual return (GSTR-9 / GSTR-9C) ────────────────────────────────
const currentFY = (() => {
  const d = new Date();
  const y = d.getFullYear();
  const startY = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `${startY}-${String(startY + 1).slice(-2)}`;
})();

const EnableAnnualModal = ({ registration, formType, onClose, onSaved }) => {
  const [fy, setFy] = useState(currentFY);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const fn = formType === 'GSTR-9' ? gstApi.enableGstr9 : gstApi.enableGstr9c;
      const res = await fn(registration.id, fy);
      toast.success(res.created ? `${formType} added for FY ${fy}` : `${formType} already exists for FY ${fy}`);
      onSaved?.();
    } catch (e) { toast.error(getApiError(e, 'Failed to enable')); }
    finally { setSaving(false); }
  };
  const label = formType === 'GSTR-9' ? 'GSTR-9 (Annual Return)' : 'GSTR-9C (GST Audit / Reconciliation)';
  return (
    <Modal title={`Enable ${label}`} onClose={onClose} testId={`gst-enable-${formType}`}>
      <div className="space-y-3">
        <p className="text-sm text-slate-300">
          This will add a single annual filing record for the selected financial year.
          Due date is set to <span className="text-emerald-400">31 December</span> of the FY-end year.
        </p>
        <Field label="Financial year" required>
          <Input value={fy} onChange={(e) => setFy(e.target.value)}
                 placeholder="e.g. 2025-26"
                 data-testid={`gst-annual-fy-${formType}`} />
        </Field>
        <p className="text-[11px] text-slate-500">Format: YYYY-YY. Current FY suggested.</p>
        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving || !/^\d{4}-\d{2}$/.test(fy)}
                  data-testid={`gst-annual-save-${formType}`}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Enable
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Add Service picker ──────────────────────────────────────────────────────
const AddServiceModal = ({ onClose, onPickGst }) => (
  <Modal title="Add service" onClose={onClose} testId="active-services-picker">
    <div className="space-y-2">
      <p className="text-sm text-slate-400 mb-3">
        Pick a service to add to this company. More recurring services will appear here as we build them out.
      </p>
      <button onClick={onPickGst}
              data-testid="pick-service-gst"
              className="w-full text-left p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 flex items-center gap-3">
        <Receipt className="w-5 h-5 text-emerald-400 flex-none" />
        <div className="flex-1 min-w-0">
          <div className="text-white font-medium text-sm">GST — Goods & Services Tax</div>
          <div className="text-slate-500 text-[11px]">Monthly / quarterly returns, GSTR-9, GSTR-9C, LUT, pricing & payments</div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500" />
      </button>
      <div className="w-full text-left p-3 rounded-lg border border-slate-800 bg-slate-900/30 opacity-50">
        <div className="text-slate-500 text-sm">ROC Annual Filing · TDS · Income Tax Audit · Trademark Renewal…</div>
        <div className="text-slate-600 text-[11px] mt-0.5">Coming soon</div>
      </div>
    </div>
  </Modal>
);
const RegistrationCard = ({ registration, onChanged }) => {
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [enableForm, setEnableForm] = useState(null); // 'GSTR-9' or 'GSTR-9C'
  const [editProfile, setEditProfile] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const changeVerification = async (status) => {
    if (status === (registration.verification_status || 'pending')) return;
    let remark = '';
    if (status === 'verified') {
      if (!window.confirm('Mark this registration VERIFIED? This locks GSTIN, filing start month, constitution and taxpayer profile.')) return;
    } else {
      remark = window.prompt(`Remark for changing verification to "${status}" (optional):`) || '';
    }
    setVerifying(true);
    try {
      await gstApi.setVerification(registration.id, { status, remark: remark || null });
      toast.success(`Verification set to ${status}`);
      onChanged?.();
    } catch (e) { toast.error(getApiError(e, 'Failed to update verification')); }
    finally { setVerifying(false); }
  };

  const loadFilings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await gstApi.filings(registration.id, {
        page: 1, limit: 100,
        status: statusFilter === 'all' ? undefined : (statusFilter === 'overdue' ? undefined : statusFilter),
      });
      let rows = r.filings || [];
      const today = new Date().toISOString().slice(0, 10);
      if (statusFilter === 'overdue') {
        const OPEN = ['not_started', 'documents_awaited', 'under_preparation',
                      'summary_prepared', 'sent_for_client_approval',
                      'changes_requested', 'client_approved', 'ready_for_filing'];
        rows = rows.filter(f => f.due_date < today && OPEN.includes(f.status));
      } else if (statusFilter === 'nil') {
        rows = rows.filter(f => f.nil_filing?.selected);
      } else if (statusFilter === 'filed') {
        rows = rows.filter(f => ['filed', 'nil_filed'].includes(f.status));
      }
      setFilings(rows);
    } catch (e) { toast.error(getApiError(e, 'Failed to load filings')); }
    finally { setLoading(false); }
  }, [registration.id, statusFilter]);

  useEffect(() => { loadFilings(); }, [loadFilings]);

  const regen = async () => {
    setRegenerating(true);
    try {
      const r = await gstApi.regenerate(registration.id, 12);
      toast.success(`Regenerated: ${r.added} new record${r.added === 1 ? '' : 's'}`);
      loadFilings();
    } catch (e) { toast.error(getApiError(e, 'Regenerate failed')); }
    finally { setRegenerating(false); }
  };

  const archive = async () => {
    if (!window.confirm('Archive this registration? Unfiled records will be soft-deleted.')) return;
    try {
      await gstApi.deleteReg(registration.id);
      toast.success('Registration archived');
      onChanged?.();
    } catch (e) { toast.error(getApiError(e, 'Archive failed')); }
  };

  const cfg = registration;
  return (
    <div className="rounded-xl border border-emerald-900/30 p-4 bg-slate-900/30" data-testid={`gst-reg-card-${cfg.id}`}>
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-emerald-300 text-sm">{cfg.gstin}</span>
            <span className="text-slate-500">·</span>
            <span className="text-slate-300 text-sm">{cfg.state}</span>
          </div>
          <div className="text-white font-medium">{cfg.legal_name}</div>
          <div className="text-[12px] text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
            <span>Profile: <span className="text-slate-200">{cfg.taxpayer_profile}</span></span>
            <span>Frequency: <span className="text-slate-200">{cfg.filing_frequency}</span></span>
            <span>Start: <span className="text-slate-200">{cfg.filing_start_month}</span></span>
            {cfg.export_config?.applies && (
              <span className="text-emerald-400">Export: {cfg.export_config.under_lut ? 'Under LUT' : 'With IGST'}</span>
            )}
            {cfg.lut_status && cfg.lut_status !== 'not_required' && (
              <span className="text-amber-400">LUT: {cfg.lut_status}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={cfg.verification_status || 'pending'}
                  disabled={verifying}
                  onChange={(e) => changeVerification(e.target.value)}
                  data-testid={`gst-verification-${cfg.id}`}
                  className={`!w-auto !h-8 text-xs ${
                    (cfg.verification_status === 'verified') ? 'text-emerald-300 border-emerald-500/50' :
                    (cfg.verification_status === 'disputed') ? 'text-red-300 border-red-500/50' :
                    'text-amber-300 border-amber-500/50'}`}>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="disputed">Disputed</option>
          </Select>
          <button onClick={() => setEditProfile(true)}
                  data-testid={`gst-edit-profile-${cfg.id}`}
                  className="text-xs text-emerald-300 hover:text-white border border-emerald-500/40 rounded-md px-2.5 py-1.5 flex items-center gap-1">
            <ClipboardList className="w-3.5 h-3.5" /> Profile
          </button>
          <button onClick={() => setEnableForm('GSTR-9')}
                  data-testid={`gst-enable-gstr9-${cfg.id}`}
                  className="text-xs text-emerald-300 hover:text-white border border-emerald-500/40 rounded-md px-2.5 py-1.5 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> GSTR-9
          </button>
          <button onClick={() => setEnableForm('GSTR-9C')}
                  data-testid={`gst-enable-gstr9c-${cfg.id}`}
                  className="text-xs text-emerald-300 hover:text-white border border-emerald-500/40 rounded-md px-2.5 py-1.5 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> GSTR-9C
          </button>
          <button onClick={archive}
                  data-testid={`gst-archive-${cfg.id}`}
                  className="text-xs text-red-400 hover:text-red-300 border border-red-500/40 rounded-md px-2.5 py-1.5 flex items-center gap-1">
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
        </div>
      </div>

      {/* Legacy Pricing & Payment panel hidden — superseded by Recurring Billing.
          Data remains in DB; RecurringBillingPanel is the single source of truth. */}
      {/* <PricingPanel registration={cfg} onFilingsInvalidate={loadFilings} /> */}
      <RecurringBillingPanel registration={cfg} />

      <div className="mt-3">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {['all', 'overdue', 'nil', 'filed'].map((k) => (
            <button key={k} onClick={() => setStatusFilter(k)}
                    data-testid={`gst-filter-${cfg.id}-${k}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border ${
                      statusFilter === k ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                      'bg-slate-800/50 text-slate-400 border-slate-700 hover:text-white'
                    }`}>
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
        <div className="rounded-lg border border-emerald-900/30 overflow-x-auto bg-[#0d1210]">
          <table className="w-full text-xs min-w-[920px]">
            <thead className="bg-slate-900/60 text-[10.5px] text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Period</th>
                <th className="text-left px-3 py-2">Form</th>
                <th className="text-left px-3 py-2">Filing month</th>
                <th className="text-left px-3 py-2">Due date</th>
                <th className="text-left px-3 py-2">Filing status</th>
                <th className="text-right px-3 py-2">Price</th>
                <th className="text-left px-3 py-2">Payment</th>
                <th className="text-left px-3 py-2">Paid till</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-emerald-900/20">
              {loading && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin" /></td></tr>
              )}
              {!loading && filings.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500">No records match this filter.</td></tr>
              )}
              {!loading && filings.map((f) => (
                <tr key={f.id} className="hover:bg-emerald-500/5" data-testid={`gst-filing-row-${f.id}`}>
                  <td className="px-3 py-2 text-white font-medium">{f.filing_period}</td>
                  <td className="px-3 py-2 text-slate-300">{f.form_type}</td>
                  <td className="px-3 py-2 text-slate-300">{f.filing_month || '—'}</td>
                  <td className="px-3 py-2 text-slate-300">{fmtDate(f.due_date)}</td>
                  <td className="px-3 py-2">
                    {(() => { const m = gstStatusMeta(effectiveFilingStatus(f)); return (
                    <span className={`inline-block whitespace-nowrap text-[10.5px] px-2 py-0.5 rounded ${m.cls}`} title={m.description}>
                      {m.label}
                    </span>); })()}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-300">
                    {f.price != null ? INR(f.price) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10.5px] px-2 py-0.5 rounded ${
                      f.payment_coverage === 'paid' ? 'bg-emerald-500/20 text-emerald-300' :
                      f.payment_coverage === 'complimentary' ? 'bg-indigo-500/20 text-indigo-300' :
                      f.payment_coverage === 'waived' ? 'bg-slate-500/20 text-slate-300' :
                      f.payment_coverage === 'already_paid' ? 'bg-emerald-500/20 text-emerald-300' :
                      f.payment_coverage === 'invoice_issued' ? 'bg-blue-500/20 text-blue-300' :
                      f.payment_coverage === 'partly_covered' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-red-500/20 text-red-300'}`}>
                      {(f.payment_coverage || 'pending').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-400">
                    {f.paid_through_date ? fmtDate(f.paid_through_date) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setDetail(f)}
                            data-testid={`gst-open-filing-${f.id}`}
                            className="text-emerald-400 hover:text-emerald-300 text-xs">
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <FilingDetailModal filing={detail} onClose={() => setDetail(null)} onChanged={loadFilings} />
      )}
      {enableForm && (
        <EnableAnnualModal registration={cfg} formType={enableForm}
                          onClose={() => setEnableForm(null)}
                          onSaved={() => { setEnableForm(null); loadFilings(); }} />
      )}
      {editProfile && (
        <EditProfileModal registration={cfg}
                          onClose={() => setEditProfile(false)}
                          onFilingsChanged={loadFilings}
                          onSaved={() => { setEditProfile(false); onChanged?.(); }} />
      )}
    </div>
  );
};

// ─── Root section — "Active Services" wrapper (GST is one of many) ───────────
export const GstRegistrationSection = ({ companyId }) => {
  const [regs, setRegs] = useState([]);
  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await gstApi.dashboard(companyId);
      setRegs(d.registrations || []);
      setCounters(d.counters);
    } catch (e) { toast.error(getApiError(e, 'Failed to load services data')); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { if (companyId) load(); }, [companyId, load]);

  return (
    <div className="space-y-4" data-testid="active-services-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4" /> Active services
          </h3>
          <p className="text-slate-500 text-[11.5px] mt-0.5">
            Recurring services subscribed by this company. Currently supports GST; more services (ROC, TDS, audit, etc.) will appear here as we build them.
          </p>
        </div>
        <button onClick={() => setShowPicker(true)}
                data-testid="active-services-add-btn"
                className="text-xs font-medium bg-emerald-500 hover:bg-emerald-400 text-white rounded-md px-3 py-1.5 flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add service
        </button>
      </div>

      {counters && regs.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatChip icon={<ClipboardList className="w-4 h-4 text-emerald-400" />} label="GST · Upcoming" value={counters.upcoming} testId="gst-count-upcoming" />
          <StatChip icon={<ClipboardList className="w-4 h-4 text-red-400" />} label="GST · Overdue" value={counters.overdue} testId="gst-count-overdue" cls="border-red-500/25" />
          <StatChip icon={<FileText className="w-4 h-4 text-yellow-400" />} label="GST · Nil selected" value={counters.nil} testId="gst-count-nil" />
          <StatChip icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} label="GST · Filed" value={counters.filed} testId="gst-count-filed" />
        </div>
      )}

      {loading && (
        <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin" /></div>
      )}
      {!loading && regs.length === 0 && (
        <div className="py-8 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">
          No active services yet. Click <span className="text-emerald-400">Add service</span> to begin.
        </div>
      )}

      {regs.length > 0 && (
        <>
          <div className="text-[11px] text-emerald-400 uppercase tracking-wider flex items-center gap-1.5 pt-2 pl-1">
            <Receipt className="w-3 h-3" /> GST
          </div>
          {regs.map((r) => (
            <RegistrationCard key={r.id} registration={r} onChanged={load} />
          ))}
        </>
      )}

      {showPicker && (
        <AddServiceModal onClose={() => setShowPicker(false)}
                        onPickGst={() => { setShowPicker(false); setShowAdd(true); }} />
      )}
      {showAdd && (
        <AddRegistrationModal companyId={companyId}
                              onClose={() => setShowAdd(false)}
                              onSaved={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
};

const StatChip = ({ icon, label, value, cls, testId }) => (
  <div className={`rounded-md p-2.5 bg-slate-900/40 border ${cls || 'border-slate-700'}`} data-testid={testId}>
    <div className="text-[11px] text-slate-400 flex items-center gap-1.5">{icon} {label}</div>
    <div className="text-lg font-bold text-white mt-1">{value}</div>
  </div>
);

export default GstRegistrationSection;
